import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { MissionInterface } from '@/interfaces/Missions.interface';
import type { PipelineInterface } from '@interfaces/Pipeline.interface';
import type { PipelineLeaderboardResponse } from '@interfaces/PipelineLeaderboard.interface';
import type { PipelineOverviewResponse } from '@interfaces/PipelineOverview.interface';
import type { PipelineStageTaskPaginatedResponse } from '@/interfaces/PipelineStageTask.interface';
import type { PipelineStageInterface } from '@interfaces/PipelineStage.interface';
import type { TargetTrackingData } from '@interfaces/TargetTracking.interface';
import { useApiClient } from '../context';
import type { ListDataResponse } from '../types';
import { shallowCopyListData } from '../shallow-copy-list-data';
import {
  pipelineQueryKeys,
  type PipelineListQueryParams,
  type PipelineMissionsQueryParams,
  type PipelineOptionsQueryParams,
  type PipelineOverviewQueryParams,
  type PipelineStageTasksQueryParams
} from './query-keys';
import type { MyWorkstreamInterface } from './my-workstreams';

export function usePipelineAllQuery(
  version: string | undefined,
  options?: Omit<UseQueryOptions<PipelineInterface[]>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey: pipelineQueryKeys.allActive(version),
    queryFn: () => api.pipeline.all(version),
    enabled: enabledOption ?? true
  });
}

export function usePipelineListQuery(
  params: PipelineListQueryParams,
  options?: Omit<
    UseQueryOptions<ListDataResponse<PipelineInterface>>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.list(params),
    queryFn: () =>
      api.pipeline.list(
        shallowCopyListData(params.listData),
        params.searchTerm,
        params.version
      )
  });
}

export function usePipelineQuery(
  id: number | undefined,
  options?: Omit<UseQueryOptions<PipelineInterface>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      id != null
        ? pipelineQueryKeys.detail(id)
        : [...pipelineQueryKeys.details(), 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('Pipeline id is required');
      return api.pipeline.get(id);
    },
    enabled: (enabledOption ?? true) && id != null
  });
}

export function usePipelineStagesQuery(
  id: number | undefined,
  version: string | undefined,
  options?: Omit<
    UseQueryOptions<PipelineStageInterface[]>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      id != null
        ? pipelineQueryKeys.stages(id, version)
        : [...pipelineQueryKeys.all, 'stages', 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('Pipeline id is required');
      return api.pipeline.getPipelineStages(id, version);
    },
    enabled: (enabledOption ?? true) && id != null
  });
}

export function usePipelineStageTasksQuery(
  params: PipelineStageTasksQueryParams | null,
  options?: Omit<
    UseQueryOptions<PipelineStageTaskPaginatedResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      params != null
        ? pipelineQueryKeys.stageTasks(params)
        : [...pipelineQueryKeys.all, 'stageTasks', 'disabled'],
    queryFn: () => {
      if (params == null) throw new Error('Stage tasks params are required');
      return api.pipeline.getPipelineStageTasks(
        params.stageId,
        params.page ?? 1,
        params.filters
      );
    },
    enabled: (enabledOption ?? true) && params != null
  });
}

export function usePipelineTasksQuery(
  id: number | undefined,
  options?: Omit<UseQueryOptions<unknown>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      id != null
        ? pipelineQueryKeys.pipelineTasks(id)
        : [...pipelineQueryKeys.all, 'pipelineTasks', 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('Pipeline id is required');
      return api.pipeline.getPipelineTasks(id);
    },
    enabled: (enabledOption ?? true) && id != null
  });
}

export function usePipelineMyPipelinesQuery(
  options?: Omit<UseQueryOptions<PipelineInterface[]>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.myPipelines(),
    queryFn: () => api.pipeline.getMyPipelines()
  });
}

export function usePipelineMyWorkstreamsQuery(
  options?: Omit<
    UseQueryOptions<MyWorkstreamInterface[]>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey: pipelineQueryKeys.myWorkstreams(),
    queryFn: () => api.pipeline.getMyWorkstreams(),
    staleTime: 30 * 60 * 1000,
    enabled: enabledOption ?? true
  });
}

export function usePipelineContractStagesQuery(
  options?: Omit<
    UseQueryOptions<PipelineStageInterface[]>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.contractStages(),
    queryFn: () => api.pipeline.getContractStages()
  });
}

export function usePipelineTargetTrackingQuery(
  id: number | undefined,
  userIds: number[] | undefined,
  options?: Omit<UseQueryOptions<TargetTrackingData>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      id != null
        ? pipelineQueryKeys.targetTracking(id, userIds)
        : [...pipelineQueryKeys.all, 'targetTracking', 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('Pipeline id is required');
      return api.pipeline.getTargetTracking(id, userIds);
    },
    enabled: (enabledOption ?? true) && id != null
  });
}

export function usePipelineMyTargetTrackingQuery(
  id: number | undefined,
  options?: Omit<UseQueryOptions<TargetTrackingData>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      id != null
        ? pipelineQueryKeys.myTargetTracking(id)
        : [...pipelineQueryKeys.all, 'myTargetTracking', 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('Pipeline id is required');
      return api.pipeline.getMyTargetTracking(id);
    },
    enabled: (enabledOption ?? true) && id != null
  });
}

export function usePipelineMissionsQuery(
  params: PipelineMissionsQueryParams | null,
  options?: Omit<
    UseQueryOptions<ListDataResponse<MissionInterface>>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      params != null
        ? pipelineQueryKeys.missions(params)
        : [...pipelineQueryKeys.all, 'missions', 'disabled'],
    queryFn: () => {
      if (params == null) throw new Error('Pipeline missions params required');
      return api.pipeline.getPipelineMissions(
        params.pipelineId,
        shallowCopyListData(params.listData)
      );
    },
    enabled: (enabledOption ?? true) && params != null
  });
}

export function usePipelineDataPointsQuery(
  businessUnitId: number | undefined,
  status: number | undefined,
  options?: Omit<
    UseQueryOptions<{
      active_tasks: number;
      conversion_rate: number;
      current_value: number;
      weighted_value: number;
      won_revenue: number;
      target_revenue: number;
      tooltip: string;
    }>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.dataPoints(businessUnitId, status),
    queryFn: () => api.pipeline.getDataPoints(businessUnitId, status)
  });
}

export function usePipelineOverviewQuery(
  params: PipelineOverviewQueryParams = {},
  options?: Omit<
    UseQueryOptions<PipelineOverviewResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.overview(params),
    queryFn: () => api.pipeline.getOverview(params)
  });
}

export function usePipelineOptionsQuery(
  params: PipelineOptionsQueryParams = {},
  options?: Omit<
    UseQueryOptions<PipelineInterface[]>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.options(params),
    queryFn: () =>
      api.pipeline.options(params.version, {
        businessUnitId: params.businessUnitId,
        pipelineType: params.pipelineType
      })
  });
}

export function usePipelineLeaderboardQuery(
  period: string,
  options?: Omit<
    UseQueryOptions<PipelineLeaderboardResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: pipelineQueryKeys.leaderboard(period),
    queryFn: () => api.pipeline.getLeaderboard(period)
  });
}
